import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Exact as D, invoiceCalc, tobaccoFeeOf, type InvoiceDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { Icon } from "../../components/Icon";
import { agentCall } from "../../lib/agent";
import {
  hasOffline, offlineCall, type OfflineItem, type OfflinePayload, type OfflineRegisterData, type OfflineSale, type OfflineSellResult, type OfflineStatus,
  type OfflineUnit
} from "../../lib/desktop";
import { receiptDoc } from "../../print/documents";
import { agentJob, printUnrecorded, type AgentJob, type PlainView } from "../../print/direct";
import type { PrintImage } from "../../print/render";
import { amt } from "../ledger/common";
import "../../styles/pos.css";

// The offline register of Delta POS for Windows (docs/offline-pos.md). It runs from the copy of Delta's web app kept
// on this PC (delta-offline://app/pos/offline) when Delta cannot be reached, with what the register was prepared with
// for the cashier's open shift. The app's main process records each sale on disk before this screen says it is done
// (desktop/src/main/offline/service.ts) and sends it to Delta when it answers; this screen never reaches Delta itself.
//
// Offline the register sells for cash into the shift's drawer only, to walk-in customers, at list prices, without
// discounts; refunds, drafts, credit and card sales wait for Delta. A unit Delta marked blocked (below cost, no cost,
// no price, out of stock) is not sold: the below-cost approval needs Delta.
//
// The receipt prints at once, from the saved sale, through the app's own printing: the original under the sale's id
// (so it is printed once), copies marked COPY. A print never repeats a sale.

interface Line { unitId: string; qty: string }
type Tab = "sale" | "sales";
type Done = { sale: OfflineSale; payload: OfflinePayload; change: string | null; print: PlainView | null; copyNo: number };

const QTY = /^(0|[1-9][0-9]{0,8})(\.[0-9]{1,3})?$/;
const riyadh = (iso: string) => new Date(new Date(iso).getTime() + 3 * 3600_000).toISOString().slice(0, 16).replace("T", " ");

/** A receipt from the saved sale: Delta's receipt layout, the register's receipt number, an OFFLINE SALE banner. */
function offlineReceipt(reg: OfflineRegisterData, p: OfflinePayload, sale: OfflineSale, t: (k: string, v?: Record<string, string | number>) => string, L: (x: { nameEn: string; nameAr: string }) => string, lang: "en" | "ar", copyNo: number, logo: PrintImage | null) {
  const units = new Map(reg.catalog.items.flatMap((i) => i.units.map((u) => [u.id, { item: i, unit: u }] as const)));
  const rate = new Map(reg.catalog.taxTypes.map((x) => [x.id, x.rate]));
  const tob = reg.catalog.tobacco.enabled ? reg.catalog.tobacco : null;
  const calc = invoiceCalc(p.lines.map((l) => {
    const x = units.get(l.itemUnitId)!;
    return { qty: l.qty, price: l.price, discountPct: null, discountAmount: null, taxRate: rate.get(l.taxTypeId) ?? "0", inclusive: x.item.priceIncludesTax, fee: tobaccoFeeOf(tob, x.item.tobaccoFee, l.qty, x.unit.pieces) };
  }), null, null, { invoiceDiscount: reg.catalog.tobacco.invoiceDiscount ?? undefined });
  const named = (x: { nameEn: string; nameAr: string }) => ({ nameEn: x.nameEn, nameAr: x.nameAr });
  const inv = {
    kind: "sale", status: "completed", code: sale.receiptNo, invoiceAt: riyadh(sale.deviceTime).replace(" ", "T"), party: null, refundOf: null,
    branch: { nameEn: reg.organization.nameEn, nameAr: reg.organization.nameAr }, warehouse: { nameEn: reg.device.name, nameAr: reg.device.name },
    createdByName: reg.cashier.name, postedByName: null, shift: { number: reg.shift.number },
    lines: p.lines.map((l, i) => { const x = units.get(l.itemUnitId)!; return { item: named(x.item), unit: named(x.unit), qty: l.qty, price: l.price, total: calc.rows[i]!.total }; }),
    totalBefore: calc.totalBefore, discount: calc.discount, subtotal: calc.subtotal, tobaccoFee: calc.fee, vat: p.totals.vat, grand: p.totals.grand, tobacco: { raised: calc.raised },
    payments: p.payments.map((x) => ({ treasury: named(reg.shift.treasury), amount: x.amount })), remaining: "0.00",
    cashTendered: p.cashTendered, cashChange: p.cashTendered ? D.fmt(D.sub(D.dec(p.cashTendered), D.dec(p.totals.grand)), 2) : null
  } as unknown as InvoiceDto;
  const seller = { ...reg.seller, logo: null };
  const doc = receiptDoc(inv, seller, { t, L, lang }, { copyNo, testDocument: reg.testDocument, logo });
  doc.blocks.splice(copyNo > 0 ? 2 : 0, 0, { t: "box", text: t("offReceiptBanner"), bold: true, size: 8.5 }, { t: "space", mm: 1.5 });
  return doc;
}

async function followJob(jobId: string): Promise<PlainView> {
  for (let i = 0, misses = 0; i < 240; i++) {
    const j = await agentJob(jobId);
    if (j.body) {
      misses = 0;
      if (j.body.state === "done") return { kind: "done", printer: j.body.printer };
      if (j.body.state === "failed" || j.body.state === "cancelled") return { kind: "failed", reason: j.body.error ?? j.body.state, jobId };
      if (j.body.state === "unknown") return { kind: "unknown", reason: j.body.error ?? "unknown", jobId };
    } else if (++misses >= 5) return { kind: "unknown", reason: "agent_unreachable", jobId };
    await new Promise((res) => setTimeout(res, 500));
  }
  return { kind: "unknown", reason: "spooled_no_answer", jobId };
}

async function dataImage(url: string | null): Promise<PrintImage | null> {
  if (!url || !url.startsWith("data:image/")) return null;
  try { const img = new Image(); img.src = url; await img.decode(); return img.naturalWidth ? { src: img, w: img.naturalWidth, h: img.naturalHeight } : null; } catch { return null; }
}

export function OfflineRegister() {
  const { t, lang, setLang, L } = useLang();
  const [reg, setReg] = useState<OfflineRegisterData | null>(null);
  const [status, setStatus] = useState<OfflineStatus | null>(null);
  const [online, setOnline] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("sale");
  const [q, setQ] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [cash, setCash] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [sales, setSales] = useState<OfflineSale[]>([]);
  const logo = useRef<PrintImage | null>(null);
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => { document.title = `${t("offTitle")} · Delta`; }, [t]);

  const refresh = useCallback(async () => {
    const s = await offlineCall<{ ok: boolean; status: OfflineStatus }>("status");
    if (s?.ok) setStatus(s.status);
    const l = await offlineCall<{ ok: boolean; sales: OfflineSale[] }>("sales", { limit: 100 });
    if (l?.ok) setSales(l.sales);
  }, []);
  const probe = useCallback(async () => { const r = await offlineCall<{ ok: boolean; online: boolean }>("probe"); setOnline(!!r?.online); void refresh(); }, [refresh]);

  useEffect(() => {
    if (!hasOffline()) return;
    void offlineCall<{ ok: boolean; register: OfflineRegisterData }>("register").then(async (r) => { if (r?.ok) { setReg(r.register); logo.current = await dataImage(r.register.seller.logoDataUrl); } });
    void refresh();
    void probe();
    const id = setInterval(() => void probe(), 20_000);
    return () => clearInterval(id);
  }, [refresh, probe]);

  const units = useMemo(() => new Map((reg?.catalog.items ?? []).flatMap((i) => i.units.map((u) => [u.id, { item: i, unit: u }] as const))), [reg]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = reg?.catalog.items ?? [];
    return (s ? all.filter((i) => i.code.toLowerCase().includes(s) || i.nameEn.toLowerCase().includes(s) || i.nameAr.includes(s) || i.units.some((u) => u.barcodes.includes(q.trim()))) : all).slice(0, 80);
  }, [q, reg]);
  const calc = useMemo(() => {
    if (!reg) return null;
    const rate = new Map(reg.catalog.taxTypes.map((x) => [x.id, x.rate]));
    const tob = reg.catalog.tobacco.enabled ? reg.catalog.tobacco : null;
    const ok = lines.every((l) => QTY.test(l.qty) && units.has(l.unitId));
    if (!ok || !lines.length) return null;
    return invoiceCalc(lines.map((l) => {
      const x = units.get(l.unitId)!;
      return { qty: l.qty, price: x.unit.price, discountPct: null, discountAmount: null, taxRate: rate.get(x.item.taxTypeId ?? "") ?? "0", inclusive: x.item.priceIncludesTax, fee: tobaccoFeeOf(tob, x.item.tobaccoFee, l.qty, x.unit.pieces) };
    }), null, null, { invoiceDiscount: reg.catalog.tobacco.invoiceDiscount ?? undefined });
  }, [lines, reg, units]);

  if (!hasOffline()) return <div className="state-box" data-testid="off-not-app"><p>{t("offNotInApp")}</p></div>;
  if (!reg || !status) return <div className="state-box"><p>{t("loading")}</p></div>;

  const add = (item: OfflineItem, unit: OfflineUnit) => {
    if (unit.blocked) return;
    setDone(null);
    setLines((ls) => {
      const i = ls.findIndex((l) => l.unitId === unit.id);
      if (i < 0) return [...ls, { unitId: unit.id, qty: "1" }];
      const n = [...ls]; const cur = n[i]!; n[i] = { ...cur, qty: QTY.test(cur.qty) ? D.fmt(D.add(D.dec(cur.qty), D.dec("1"))).replace(/\.0+$/, "") : "1" }; return n;
    });
    void item;
  };
  const onScan = () => {
    const code = q.trim();
    for (const i of reg.catalog.items) for (const u of i.units) if (u.barcodes.includes(code) || (i.code === code && i.units[0] === u)) { add(i, u); setQ(""); return; }
  };
  const why = (r: Extract<OfflineSellResult, { ok: false }>) => t(`offWhy_${r.reason}`, { n: r.lineNo ?? 0, x: r.detail ? (t(`offBlocked_${r.detail}`) || r.detail) : "" });

  const print = async (d: Done, copyNo: number, retryJob?: string) => {
    setDone({ ...d, print: { kind: "working" }, copyNo });
    const ref = `offline:${d.sale.saleId}:${copyNo === 0 ? "original" : `copy:${copyNo}`}`;
    let v: PlainView;
    if (retryJob) {
      // A failed job printed nothing: the same job again (same id, same pages), followed to its end.
      const r = await agentCall<AgentJob>("POST", `/print/jobs/${retryJob}/retry`, {});
      v = r.body ? await followJob(retryJob) : { kind: "failed", reason: r.code ?? "retry_refused", jobId: retryJob };
    } else {
      v = await printUnrecorded("receipt", ref, `Receipt ${d.sale.receiptNo}`, () => offlineReceipt(reg, d.payload, d.sale, t, L, lang, copyNo, logo.current), reg.cashier.name, () => undefined,
        { jobId: copyNo === 0 ? d.sale.saleId : undefined, copy: copyNo > 0 });
    }
    setDone((x) => (x && x.sale.saleId === d.sale.saleId ? { ...x, print: v, copyNo } : x));
  };
  const printCopy = async (d: Done) => {
    const r = await offlineCall<{ ok: boolean; prints: number }>("countPrint", { saleId: d.sale.saleId });
    if (r?.ok) await print(d, Math.max(r.prints - 1, 1));
  };

  const complete = async (confirmTotal?: string) => {
    if (busy || !lines.length) return;
    setBusy(true); setProblem(null);
    try {
      const r = await offlineCall<OfflineSellResult>("sell", { lines: lines.map((l) => ({ itemUnitId: l.unitId, qty: l.qty })), cashTendered: cash.trim() || null, confirmTobaccoTotal: confirmTotal ?? null });
      if (!r) { setProblem(t("offWhy_store_failed", { x: "" })); return; }
      if (!r.ok) { if (r.reason === "confirm_total" && r.revisedTotal) setConfirm(r.revisedTotal); else setProblem(why(r)); return; }
      setConfirm(null); setLines([]); setCash("");
      const d: Done = { sale: r.sale, payload: r.payload, change: r.change, print: null, copyNo: 0 };
      setDone(d);
      void refresh();
      // The original receipt, once: counted, then printed under the sale's id.
      await offlineCall("countPrint", { saleId: r.sale.saleId });
      await print(d, 0);
    } finally { setBusy(false); search.current?.focus(); }
  };

  const printText = (v: PlainView | null) => !v ? null : v.kind === "working" ? t("offPrinting") : v.kind === "done" ? t("offPrinted", { p: v.printer ?? "" })
    : v.reason === "no_printer" || v.reason === "not_paired" ? t("offPrintNone") : v.kind === "failed" ? t("offPrintFailed", { x: v.reason }) : t("offPrintUnknown", { x: v.reason });

  return (
    <div className="pos" data-testid="offline-register">
      <header className="pos-top">
        <div className="pos-brand"><b>Delta</b><span className="pos-badge" data-testid="off-badge">{t("offTitle")}</span></div>
        <nav className="pos-tabs" aria-label={t("offTitle")}>
          <a href="#sale" className={tab === "sale" ? "active" : ""} onClick={(e) => { e.preventDefault(); setTab("sale"); }}><Icon name="cart" /><span>{t("offSell")}</span></a>
          <a href="#sales" className={tab === "sales" ? "active" : ""} onClick={(e) => { e.preventDefault(); setTab("sales"); void refresh(); }} data-testid="off-tab-sales"><Icon name="receipt" /><span>{t("offSales")}</span>
            {status.pending + status.held + status.failed > 0 && <span className="num"> ({status.pending + status.held + status.failed})</span>}</a>
        </nav>
        <div className="pos-tools">
          <button className="pos-icon-btn pos-lang" onClick={() => setLang(lang === "ar" ? "en" : "ar")} lang={lang === "ar" ? "en" : "ar"}><Icon name="globe" /><span>{t("switchLang")}</span></button>
          <span className="pos-user"><span className="pu-name">{reg.cashier.name}</span></span>
        </div>
      </header>
      <main className="pos-main">
        <div className={`assume ${online ? "" : "warn"}`} role="status" data-testid="off-connection" style={{ margin: "8px 12px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`pill ${online ? "good" : "bad"}`} data-testid="off-online">{online ? t("offOnline") : t("offOffline")}</span>
          <span>{online ? "" : t("offBanner")}</span>
          <span data-testid="off-counts">{t("offPending")}: <b className="num" data-testid="off-pending">{status.pending}</b> · {t("offHeld")}: <b className="num">{status.held}</b> · {t("offFailed")}: <b className="num">{status.failed}</b></span>
          {status.lastSyncError && <span className="hint">{t("offLastError")}: {status.lastSyncError === "unreachable" ? t("offErr_unreachable") : status.lastSyncError}</span>}
          <span style={{ marginInlineStart: "auto", display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={() => void offlineCall("sync").then(() => probe())} data-testid="off-sync"><Icon name="refresh" />{t("offSyncNow")}</button>
            {online ? <button className="btn btn-sm btn-primary" onClick={() => void offlineCall("leave")} data-testid="off-back">{t("offBack")}</button>
              : <button className="btn btn-sm" onClick={() => void probe()} data-testid="off-check">{t("offCheck")}</button>}
          </span>
        </div>
        <p className="hint" style={{ margin: "0 12px 8px" }} data-testid="off-shift">
          {t("offDevice", { code: reg.device.code })} · {t("offShift", { n: reg.shift.number, c: L(reg.shift.treasury) })} · {status.grant ? t("offGrantUntil", { t: riyadh(status.grant.expiresAt), u: status.grant.used, m: status.grant.maxSales }) : ""}
        </p>
        {!status.canSell && status.why && <div className="pos-err" role="alert" style={{ margin: "0 12px 8px" }} data-testid="off-refusal">{t(`offWhy_${status.why}`, { n: 0, x: "" })}</div>}
        {tab === "sale" ? (
          <div className="pos-sale">
            <section className="pos-catalog">
              <div className="cat-search input-group"><span className="lead"><Icon name="search" /></span>
                <input ref={search} className="control" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchNameBarcode")} autoComplete="off" autoFocus data-testid="off-search"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onScan(); } }} aria-label={t("searchNameBarcode")} /></div>
              <p className="hint" style={{ margin: "6px 0" }} data-testid="off-rules">{t("offRules")}</p>
              <div className="prod-grid" data-testid="off-grid">
                {shown.flatMap((it) => it.units.map((u) => (
                  <button key={u.id} className={`prod ${u.blocked ? "out" : ""}`} onClick={() => add(it, u)} disabled={!!u.blocked || !status.canSell} data-testid="off-prod" title={u.blocked ? t(`offBlocked_${u.blocked}`) : undefined}>
                    <span className="prod-name">{L(it)}</span><span className="prod-sku num">{it.code}</span>
                    <span className="prod-foot"><b className="num">{amt(u.price)}</b><span className="prod-unit">/ {L(u)}</span></span>
                    {u.blocked ? <span className="stock bad">{t(`offBlocked_${u.blocked}`)}</span> : it.hasQty && <span className="stock">{t("inStock")} · <span className="num">{(it.stock ?? "0").replace(/\.0+$/, "")}</span></span>}
                  </button>)))}
              </div>
            </section>
            <aside className="pos-cart" aria-label={t("currentSale")}>
              <div className="cart-head"><div className="cart-title"><h2>{t("currentSale")}</h2><span className="count num" data-testid="off-cart-count">{lines.length}</span>
                <button className="btn btn-sm btn-ghost" disabled={!lines.length} onClick={() => setLines([])}><Icon name="trash" />{t("clearCart")}</button></div></div>
              <div className="cart-lines">
                {!lines.length && !done && <div className="empty cart-empty"><Icon name="cart" /><div>{t("emptyCart")}</div></div>}
                {lines.map((l, i) => { const x = units.get(l.unitId)!; return (
                  <div key={l.unitId} className="cl" data-testid="off-line"><div className="cl-main">
                    <span className="cl-name"><b>{L(x.item)}</b><span>{L(x.unit)} · <span className="num">{amt(x.unit.price)}</span></span></span>
                    <div className="stepper"><input className="num" inputMode="decimal" value={l.qty} onChange={(e) => setLines((ls) => ls.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} aria-label={t("qty")} data-testid="off-qty" /></div>
                    <div className="cl-total num">{calc ? amt(calc.rows[i]!.total) : "–"}</div>
                    <button className="cl-del" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label={t("deleteRow")}><Icon name="trash" /></button>
                  </div></div>); })}
                {done && (
                  <div className="card" data-testid="off-done" style={{ margin: 12 }}><div className="card-body">
                    <p><b data-testid="off-saved">{t("offSaved", { r: done.sale.receiptNo })}</b></p>
                    <p className="num">{t("grandTotal")}: {amt(done.payload.totals.grand)} {t("currency")}{done.change ? ` · ${t("changeDue")}: ${amt(done.change)}` : ""}</p>
                    <p data-testid="off-print" className={done.print?.kind === "done" ? "" : "hint"}>{printText(done.print)}</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {done.print?.kind === "failed" && done.print.jobId && <button className="btn btn-sm" onClick={() => void print(done, done.copyNo, (done.print as { jobId: string }).jobId)} data-testid="off-retry-print">{t("offRetryPrint")}</button>}
                      <button className="btn btn-sm" onClick={() => void printCopy(done)} disabled={done.print?.kind === "working"} data-testid="off-copy">{t("offPrintCopy")}</button>
                      <button className="btn btn-sm btn-primary" onClick={() => { setDone(null); search.current?.focus(); }}>{t("offNewSale")}</button>
                    </div></div></div>)}
              </div>
              <div className="cart-foot">
                <div className="cart-totals" data-testid="off-totals">
                  <div className="ct-row"><span>{t("subTotal")}</span><span className="num">{amt(calc?.subtotal ?? "0.00")}</span></div>
                  {calc && calc.fee !== "0.00" && <div className="ct-row"><span>{t("tobFee")}</span><span className="num">{amt(calc.fee)}</span></div>}
                  <div className="ct-row"><span>{t("tax")}</span><span className="num">{amt(calc?.vat ?? "0.00")}</span></div>
                  <div className="ct-grand"><span>{t("grandTotal")}</span><span><span className="num" data-testid="off-grand">{amt(calc?.grand ?? "0.00")}</span> <span className="cur">{t("currency")}</span></span></div>
                </div>
                <div className="pos-tender"><label className="field"><span className="label">{t("cashReceived")}</span>
                  <input className="control num" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} data-testid="off-cash" /></label>
                  <div className="pos-change"><span className="label">{t("changeDue")}</span><b className="num">{calc && cash.trim() && /^\d+(\.\d{1,2})?$/.test(cash.trim()) && D.cmp(D.dec(cash.trim()), D.dec(calc.grand)) >= 0 ? amt(D.fmt(D.sub(D.dec(cash.trim()), D.dec(calc.grand)), 2)) : "–"}</b></div></div>
                {problem && <div className="pos-err" role="alert" data-testid="off-problem">{problem}</div>}
                {confirm && <div className="cl-warn" role="alert" data-testid="off-confirm"><span>{t("offConfirmTotal", { x: amt(confirm) })}</span>
                  <button className="btn btn-sm btn-primary" onClick={() => void complete(confirm)}>{t("offConfirm", { x: amt(confirm) })}</button></div>}
                <button className="btn btn-primary pay-btn" disabled={busy || !lines.length || !calc || !status.canSell} onClick={() => void complete()} data-testid="off-complete"><Icon name="check" />{t("offComplete")}</button>
              </div>
            </aside>
          </div>
        ) : (
          <div className="pos-page"><section className="card"><div className="card-body">
            {!sales.length ? <p className="hint">{t("offNoSales")}</p> : (
              <table className="pos-tbl" data-testid="off-sales"><thead><tr><th>{t("offReceiptNo")}</th><th>{t("date")}</th><th>{t("grandTotal")}</th><th>{t("regsStatus")}</th><th>{t("offInvoice")}</th><th /></tr></thead>
                <tbody>{sales.map((s) => { const p = JSON.parse(s.payload) as OfflinePayload; return (
                  <tr key={s.saleId} data-testid="off-sale-row"><td className="num">{s.receiptNo}</td><td className="num">{riyadh(s.deviceTime)}</td><td className="num">{amt(p.totals.grand)}</td>
                    <td><span className={`pill ${s.state === "synced" ? "good" : s.state === "pending" ? "warn" : "bad"}`} data-testid="off-sale-state">{t(`offState_${s.state}`)}</span>
                      {(s.heldBy || (s.state !== "synced" && s.lastError)) && <div className="hint">{s.heldBy ?? (s.lastError === "unreachable" ? t("offErr_unreachable") : s.lastError)}</div>}</td>
                    <td className="num">{s.invoiceCode ?? "–"}</td>
                    <td><button className="btn btn-sm" onClick={() => { const d: Done = { sale: s, payload: p, change: null, print: null, copyNo: 0 }; setTab("sale"); setDone(d); void printCopy(d); }} data-testid="off-sale-copy">{t("offPrintCopy")}</button></td></tr>); })}</tbody></table>)}
          </div></section></div>
        )}
      </main>
    </div>
  );
}
